import React, { useEffect, useState } from "react";
import { KeyRound, Loader2, Check, Trash2, Mail, Phone, RefreshCw, Save } from "lucide-react";
import { API } from "../api";

export default function AdminAccessRequests() {
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState("");
  const [codes, setCodes] = useState({});     // slug -> code (édition en cours)
  const [savingCode, setSavingCode] = useState("");

  const load = () => {
    fetch(`${API}/admin/access-requests`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => {
        const list = d.requests || [];
        setReqs(list);
        const map = {};
        list.forEach((r) => { if (r.slug && !(r.slug in map)) map[r.slug] = r.access_code || ""; });
        setCodes(map);
      })
      .catch(() => setReqs([]));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await fetch(`${API}/admin/access-requests/${id}/status`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
      });
      setReqs((rs) => rs.map((r) => r.id === id ? { ...r, status } : r));
    } catch (_) {}
    setBusy("");
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    setBusy(id);
    try {
      await fetch(`${API}/admin/access-requests/${id}`, { method: "DELETE", credentials: "include" });
      setReqs((rs) => rs.filter((r) => r.id !== id));
    } catch (_) {}
    setBusy("");
  };

  // Enregistre un code d'accès type pour l'annonce (réutilisable dans toutes les réponses de ce bien).
  const saveCode = async (slug) => {
    setSavingCode(slug);
    try {
      const res = await fetch(`${API}/admin/listings/${slug}/access-code`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code: codes[slug] || "" }),
      });
      const d = res.ok ? await res.json() : {};
      const eff = d.access_code ?? (codes[slug] || "");
      setCodes((c) => ({ ...c, [slug]: eff }));
      setReqs((rs) => rs.map((r) => r.slug === slug ? { ...r, access_code: eff } : r));
    } catch (_) {}
    setSavingCode("");
  };

  // Répondre au prospect : insère AUTOMATIQUEMENT le code d'accès type de l'annonce + marque « Code envoyé ».
  const respond = (r) => {
    const code = codes[r.slug] || r.access_code || "[ VOTRE CODE ]";
    const subject = encodeURIComponent(`OFF MARKETTE — Votre code d'accès pour « ${r.listing_title || r.slug} »`);
    const body = encodeURIComponent(`Bonjour ${r.name || ""},\n\nSuite à votre demande, voici le code d'accès pour découvrir le bien « ${r.listing_title || r.slug} » :\n\n${code}\n\nRestant à votre disposition,\n`);
    if (r.email) window.open(`mailto:${r.email}?subject=${subject}&body=${body}`, "_blank");
    setStatus(r.id, "sent");
  };

  if (!reqs) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-access-requests">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-[#111827] flex items-center gap-2"><KeyRound className="w-6 h-6 text-[#C19A6B]" /> Demandes de code d'accès</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{reqs.length} demande{reqs.length > 1 ? "s" : ""} · {reqs.filter((r) => (r.status || "pending") === "pending").length} en attente</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#C19A6B] border border-[#C19A6B]/40 rounded-[5px] px-3 py-2 hover:bg-[#C19A6B]/5"><RefreshCw className="w-3.5 h-3.5" /> Rafraîchir</button>
      </div>

      {reqs.length === 0 ? (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-10 text-center text-sm text-zinc-500">Aucune demande de code pour l'instant.</div>
      ) : (
        <div className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden divide-y divide-[#F3F4F6]">
          {reqs.map((r) => {
            const sent = r.status === "sent";
            return (
              <div key={r.id} data-testid={`access-req-${r.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#111827]">{r.name || "Sans nom"}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${sent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{sent ? "Code envoyé" : "En attente"}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5 truncate">Bien : <span className="text-[#C19A6B]">{r.listing_title || r.slug}</span></p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-600">
                    {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>}
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                  </div>
                  {r.project && <p className="text-xs text-zinc-400 mt-1 italic">« {r.project} »</p>}
                  {/* Modèle de code d'accès de l'annonce (éditable, réutilisable) */}
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-wider text-zinc-400 shrink-0">Code type</label>
                    <input
                      value={codes[r.slug] ?? ""}
                      onChange={(e) => setCodes((c) => ({ ...c, [r.slug]: e.target.value }))}
                      data-testid={`access-code-input-${r.id}`}
                      placeholder="ex. SAFTI60"
                      className="w-32 text-xs font-mono tracking-wider bg-[#F9FAFB] border border-[#E5E7EB] rounded-[5px] px-2 py-1.5 text-[#111827] focus:outline-none focus:border-[#C19A6B]/60" />
                    <button onClick={() => saveCode(r.slug)} disabled={savingCode === r.slug} data-testid={`access-code-save-${r.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#C19A6B] border border-[#C19A6B]/40 rounded-[5px] px-2 py-1.5 hover:bg-[#C19A6B]/5 disabled:opacity-50">
                      {savingCode === r.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.email && (
                    <button onClick={() => respond(r)} disabled={busy === r.id} data-testid={`access-req-respond-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C19A6B] border border-[#C19A6B]/40 rounded-[5px] px-3 py-2 hover:bg-[#C19A6B]/5">
                      <Mail className="w-3.5 h-3.5" /> Répondre
                    </button>
                  )}
                  {sent ? (
                    <button onClick={() => setStatus(r.id, "pending")} disabled={busy === r.id} data-testid={`access-req-undo-${r.id}`} className="text-xs font-medium text-zinc-500 border border-[#E5E7EB] rounded-[5px] px-3 py-2 hover:bg-[#F1F3F5]">Rouvrir</button>
                  ) : (
                    <button onClick={() => setStatus(r.id, "sent")} disabled={busy === r.id} data-testid={`access-req-sent-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#C19A6B] hover:bg-[#A88358] text-white rounded-[5px] px-3 py-2 disabled:opacity-50">
                      {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Code envoyé
                    </button>
                  )}
                  <button onClick={() => remove(r.id)} disabled={busy === r.id} data-testid={`access-req-del-${r.id}`} className="text-zinc-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
